import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Button, Paper, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikTextField } from "../../../../../../formik/FormikTextField";
import { nameof } from "../../../../../../utils/nameof";
import { AssetPropertyRightKind, PendingDocumentMove } from "../../../../../api/ClientFileApi";
import { InventoryUploadArea } from "../../../../components/inventory-upload-area/InventoryUploadArea";
import { FieldWithInfo } from "../../../personal-data/components/field-with-info/FieldWithInfo";
import { PropertyRightsFormValues, PropertyRightsMaxLength } from "../../OtherPropertyRightsView.types";
import {
  DetailRightKindConfig,
  hasRightsOf,
  rightIndexesOfKind,
  withAddedRight,
} from "../../OtherPropertyRightsView.utils";
import { useRemoveRight } from "../../hooks/useRemoveRight";
import { EntryQuestion } from "../entry-question/EntryQuestion";

const TAB = "clients.client.debtSettlement.assetInventory.propertyRightsTab";
const RIGHTS_FIELD = nameof<PropertyRightsFormValues>("propertyRights");

interface Props {
  clientId: string;
  formik: FormikProps<PropertyRightsFormValues>;
  config: DetailRightKindConfig;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

export function DetailRightsSection({ clientId, formik, config, onAddMove, onCancelMove }: Props) {
  const { t } = useTranslation();
  const removeRight = useRemoveRight(formik);
  const kindKey = `${TAB}.${config.translationKey}` as const;
  const rights = formik.values.propertyRights;
  const blocks = rightIndexesOfKind(rights, config.kind).flatMap((index) => {
    const right = rights[index];
    return right ? [{ index, key: right.key }] : [];
  });

  const entryInfo =
    config.kind === AssetPropertyRightKind.CooperativeMembership
      ? t(`${TAB}.cooperativeMemberships.entryInfo`)
      : undefined;
  const detailsInfo =
    config.kind === AssetPropertyRightKind.CompanyShare ? t(`${TAB}.companyShares.detailsInfo`) : undefined;

  return (
    <Stack gap={2}>
      <EntryQuestion formik={formik} config={config} info={entryInfo} />
      {hasRightsOf(formik.values.answers, config.kind) && (
        <>
          {blocks.map(({ index, key }, position) => {
            const number = position + 1;
            const title = t(`${kindKey}.blockTitle`, { number });
            const detailsLabel = t(number > 1 ? `${kindKey}.detailsFurther` : `${kindKey}.details`);
            const details = (
              <FormikTextField
                id={`property-rights-${key}-details`}
                formik={formik}
                path={`propertyRights.${index}.details`}
                label={detailsLabel}
                multiline
                minRows={3}
                slotProps={{ input: { inputProps: { maxLength: PropertyRightsMaxLength.text } } }}
              />
            );

            return (
              <Paper key={key} variant="outlined" component="article" aria-label={title} sx={{ p: 2 }}>
                <Stack gap={2}>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                    {title}
                  </Typography>
                  {detailsInfo ? (
                    <FieldWithInfo fieldLabel={detailsLabel} info={detailsInfo}>
                      {details}
                    </FieldWithInfo>
                  ) : (
                    details
                  )}
                  <Button
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => removeRight(index)}
                    sx={{ alignSelf: "flex-end" }}
                  >
                    {t(`${kindKey}.remove`, { number })}
                  </Button>
                </Stack>
              </Paper>
            );
          })}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() =>
              void formik.setFieldValue(RIGHTS_FIELD, withAddedRight(formik.values.propertyRights, config.kind))
            }
            sx={{ alignSelf: "flex-end" }}
          >
            {t(`${kindKey}.add`)}
          </Button>
          <InventoryUploadArea
            clientId={clientId}
            formik={formik}
            config={config}
            label={t(`${kindKey}.upload`)}
            onAddMove={onAddMove}
            onCancelMove={onCancelMove}
          />
        </>
      )}
    </Stack>
  );
}
